import { RightOutlined } from '@ant-design/icons'
import {
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Button, Modal } from 'antd'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import Loader from '@/components/Atoms/Loader/Loader'
import { PrioritySaveBar } from '@/components/PrioritySortShared/PrioritySaveBar/PrioritySaveBar'
import {
  PrioritySortRow,
  SortablePriorityRow,
} from '@/components/PrioritySortShared/PrioritySortRow/PrioritySortRow'
import cn from '@/components/PrioritySortShared/PrioritySortShared.module.scss'
import {
  getComparableState,
  getPrioritizedCountAfterReorder,
  hasComparableStateChanged,
} from '@/components/PrioritySortShared/priorityState'
import { MAX_PRIORITY } from '@/constants'
import { usePriorityHistory } from '@/hooks/ui/usePriorityHistory'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { cleanTechnicalSuffix } from '@/utils/catalog/cleanDescription'

interface CategoryNode {
  category: {
    id: string
    code: number
    description: string
    priority?: number
  }
  childCategories?: CategoryNode[]
}

type OrdersMap = Record<string, string[]>
type NodesMap = Record<string, CategoryNode>

const ROOT_KEY = 'root'

const buildMaps = (
  nodes: CategoryNode[],
  parentKey: string,
  ordersMap: OrdersMap,
  nodesMap: NodesMap
) => {
  ordersMap[parentKey] = nodes.map((n) => n.category.id)
  nodes.forEach((node) => {
    nodesMap[node.category.id] = node
    if (node.childCategories?.length) {
      buildMaps(node.childCategories, node.category.id, ordersMap, nodesMap)
    }
  })
}

const ExpandButton = memo(
  ({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) => (
    <Button
      size="small"
      type="text"
      icon={
        <RightOutlined
          className={`${cn.expandIcon} ${isExpanded ? cn.expandIconOpen : ''}`}
        />
      }
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn.expandButton}
    />
  )
)

const ExcelDnDPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [originalOrdersMap, setOriginalOrdersMap] = useState<OrdersMap>({})
  const [originalPriorityMap, setOriginalPriorityMap] = useState<
    Record<string, number | undefined>
  >({})
  const [nodesMap, setNodesMap] = useState<NodesMap>({})
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeParentKey, setActiveParentKey] = useState<string | null>(null)
  const dragHappenedRef = useRef(false)
  const [modal, contextHolder] = Modal.useModal()
  const { selectedOrganisation } = useUserInfoStore()
  const { incrementCategoriesPrioritiesVersion } = useTreeTilesStore()

  const {
    state: ordersMap,
    push,
    undo,
    redo,
    reset,
    canUndo,
    canRedo,
  } = usePriorityHistory<OrdersMap>({})

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const fetchTree = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get('/categories/root-trees')
      const trees: CategoryNode[] = response.data.trees || response.data || []
      const newOrdersMap: OrdersMap = {}
      const newNodesMap: NodesMap = {}
      const newPriorityMap: Record<string, number | undefined> = {}
      buildMaps(trees, ROOT_KEY, newOrdersMap, newNodesMap)
      Object.values(newNodesMap).forEach((node) => {
        newPriorityMap[node.category.id] = node.category.priority
      })
      reset(newOrdersMap)
      setOriginalOrdersMap(structuredClone(newOrdersMap))
      setOriginalPriorityMap(newPriorityMap)
      setNodesMap(newNodesMap)
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }, [modal, reset])

  useEffect(() => {
    fetchTree()
  }, [fetchTree])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveId(null)
      setActiveParentKey(null)
      if (!over || active.id === over.id) return

      const activeParent = active.data.current?.parentKey
      const overParent = over.data.current?.parentKey
      if (!activeParent || activeParent !== overParent) return

      const ids = ordersMap[activeParent] || []
      const oldIndex = ids.indexOf(active.id.toString())
      const newIndex = ids.indexOf(over.id.toString())
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

      push({ ...ordersMap, [activeParent]: arrayMove(ids, oldIndex, newIndex) })
    },
    [ordersMap, push]
  )

  const changedIds = useMemo(() => {
    const changed = new Set<string>()
    Object.entries(ordersMap).forEach(([parentKey, ids]) => {
      const original = originalOrdersMap[parentKey] || []
      const initialMap = new Map<
        string,
        { isMain: boolean; priority?: number }
      >()
      original.forEach((id) => {
        initialMap.set(
          id,
          getComparableState({ priority: originalPriorityMap[id] })
        )
      })

      const prioritizedCount = getPrioritizedCountAfterReorder(
        ids,
        original,
        originalPriorityMap
      )

      ids.forEach((id, idx) => {
        const currentState = getComparableState({
          priority: idx < prioritizedCount ? idx + 1 : MAX_PRIORITY,
        })
        const originalState = initialMap.get(id)
        if (
          !originalState ||
          hasComparableStateChanged(currentState, originalState)
        ) {
          changed.add(id)
        }
      })
    })

    return changed
  }, [ordersMap, originalOrdersMap, originalPriorityMap])

  const handleSave = useCallback(async () => {
    if (changedIds.size === 0) return
    const updates: Record<string, number> = {}
    Object.entries(ordersMap).forEach(([parentKey, ids]) => {
      const original = originalOrdersMap[parentKey] || []
      const prioritizedCount = getPrioritizedCountAfterReorder(
        ids,
        original,
        originalPriorityMap
      )
      ids.forEach((id, idx) => {
        if (changedIds.has(id)) {
          updates[id] = idx < prioritizedCount ? idx + 1 : MAX_PRIORITY
        }
      })
    })

    const t = toast.loading('Сохранение...')
    setLoading(true)
    try {
      await api.patch('/categories/priorities', { updates })
      await fetchTree()
      incrementCategoriesPrioritiesVersion()
      toast.success('Успешно сохранено!', { id: t })
    } catch (e) {
      toast.error('Ошибка при сохранении', { id: t })
      handleApiError(e, modal)
    } finally {
      setLoading(false)
    }
  }, [
    ordersMap,
    changedIds,
    originalOrdersMap,
    originalPriorityMap,
    fetchTree,
    modal,
    incrementCategoriesPrioritiesVersion,
  ])

  const activeNode = activeId ? nodesMap[activeId] : null
  const activeIndex =
    activeParentKey && activeId
      ? (ordersMap[activeParentKey] || []).indexOf(activeId)
      : -1

  const renderLevel = (parentKey: string): React.ReactNode => {
    const ids = ordersMap[parentKey] || []
    if (ids.length === 0) return null
    const original = originalOrdersMap[parentKey] || []
    const prioritizedCount = getPrioritizedCountAfterReorder(
      ids,
      original,
      originalPriorityMap
    )

    return (
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {ids.map((id, index) => {
          const node = nodesMap[id]
          if (!node) return null
          const hasChildren = (ordersMap[id] || []).length > 0
          const isExpanded = expandedIds.has(id)

          return (
            <div key={id} className={cn.rootLevel}>
              <SortablePriorityRow
                id={id}
                parentKey={parentKey}
                index={index}
                name={cleanTechnicalSuffix(node.category.description)}
                code={node.category.code}
                isChanged={changedIds.has(id)}
                onClick={
                  hasChildren
                    ? () => {
                        if (dragHappenedRef.current) return
                        toggleExpanded(id)
                      }
                    : undefined
                }
                extra={
                  <div className={cn.rowExtraInline}>
                    {index < prioritizedCount && (
                      <span className={cn.priorityBadge}>
                        Приоритет: {index + 1}
                      </span>
                    )}
                    <div className={cn.expandSlot}>
                      {hasChildren && (
                        <ExpandButton
                          isExpanded={isExpanded}
                          onToggle={() => toggleExpanded(id)}
                        />
                      )}
                    </div>
                  </div>
                }
              />
              {hasChildren && isExpanded && (
                <div className={`${cn.childrenContainer} ${cn.childrenEnter}`}>
                  {renderLevel(id)}
                </div>
              )}
            </div>
          )
        })}
      </SortableContext>
    )
  }

  return (
    <div
      className={`${cn.container} ${Object.keys(selectedOrganisation).length > 0 ? cn.blueTheme : ''}`}
    >
      {contextHolder}
      <Loader loaderType="modal" isModalOpen={loading} />

      <div className={cn.treeWrapper}>
        <DndContext
          sensors={sensors}
          autoScroll
          onDragStart={(e) => {
            dragHappenedRef.current = true
            setActiveId(e.active.id.toString())
            setActiveParentKey(e.active.data.current?.parentKey || null)
          }}
          onDragCancel={() => {
            setActiveId(null)
            setActiveParentKey(null)
            setTimeout(() => {
              dragHappenedRef.current = false
            }, 0)
          }}
          onDragEnd={(event) => {
            handleDragEnd(event)
            setTimeout(() => {
              dragHappenedRef.current = false
            }, 0)
          }}
          modifiers={[restrictToVerticalAxis]}
        >
          <div
            className={`${cn.dropZone} ${activeId ? cn.isDraggingGlobal : ''}`}
          >
            {(ordersMap[ROOT_KEY] || []).length > 0
              ? renderLevel(ROOT_KEY)
              : !loading && (
                  <div className={cn.emptyPlaceholder}>Данные отсутствуют</div>
                )}
          </div>

          <DragOverlay
            dropAnimation={{
              duration: 150,
              easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
              sideEffects: defaultDropAnimationSideEffects({
                styles: { active: { opacity: '0.4' } },
              }),
            }}
          >
            {activeNode ? (
              <PrioritySortRow
                index={activeIndex}
                name={cleanTechnicalSuffix(activeNode.category.description)}
                code={activeNode.category.code}
                isChanged={changedIds.has(activeId!)}
                isOverlay
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <PrioritySaveBar
        changeCount={changedIds.size}
        onSave={handleSave}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        loading={loading}
      />
    </div>
  )
}

export default ExcelDnDPage
